import React, { useState } from "react";
import { toast } from "sonner";
import { Calendar, Send, CheckCircle2, ArrowRight } from "lucide-react";

const initialState = {
  firstName: "",
  lastName: "",
  businessName: "",
  website: "",
  city: "",
  postcode: "",
  email: "",
  mobile: "",
  hearAbout: "",
  kitchenStatus: "",
  halal: "",
  alcoholFree: "",
  message: "",
  agree: false,
};

const HEAR_OPTIONS = [
  "Instagram",
  "Google search",
  "Word of mouth / referral",
  "Deliveroo / Uber Eats / Just Eat",
  "Industry event",
  "Other",
];

const KITCHEN_OPTIONS = [
  "I run a takeaway, restaurant or dark kitchen that is trading",
  "I'm opening a new kitchen soon",
  "I'm exploring – no kitchen yet",
];

const JourneyForm = () => {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !form.mobile ||
      !form.agree
    ) {
      toast.error("Please fill in all required fields and accept the terms.");
      return;
    }
    const existing = JSON.parse(localStorage.getItem("kitchnworxLeads") || "[]");
    existing.push({ ...form, createdAt: new Date().toISOString() });
    localStorage.setItem("kitchnworxLeads", JSON.stringify(existing));
    setSubmitted(true);
    toast.success("Thanks! Your application is in. We’ll reply within 48 hours.");
  };

  const inputCls =
    "w-full bg-[#0E0E13] border border-white/10 rounded-xl px-4 py-3.5 text-white text-[15px] focus:border-[#14E0BF] focus:bg-[#14141B] transition-all duration-200 placeholder:text-white/30";
  const labelCls =
    "block text-white/85 text-[12px] font-mono uppercase tracking-wider mb-2";

  return (
    <section
      id="journey"
      className="relative py-24 lg:py-32 bg-[#07070A] overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-40" />
      <div className="absolute -top-24 right-1/4 w-[500px] h-[500px] rounded-full bg-[#14E0BF]/10 blur-[140px]" />
      <div className="absolute -bottom-24 left-1/4 w-[400px] h-[400px] rounded-full bg-[#F77F00]/8 blur-[140px]" />

      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <div className="inline-flex items-center gap-2 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#14E0BF] animate-pulse-dot" />
                <span className="font-mono text-[11px] text-[#14E0BF] uppercase tracking-[0.2em]">
                  Apply Now
                </span>
              </div>
              <h2 className="text-white mb-6">
                <span className="font-serif-display italic text-5xl lg:text-7xl leading-[0.95] block">
                  Tell us about
                </span>
                <span className="font-display text-5xl lg:text-7xl leading-[0.95] block mt-1">
                  YOUR <span className="text-[#14E0BF]">KITCHEN.</span>
                </span>
              </h2>
              <p className="text-white/70 text-base lg:text-lg leading-relaxed mb-10 max-w-[440px]">
                One short form. We don&rsquo;t share your data, we don&rsquo;t auto-enrol you in newsletters and we don&rsquo;t hard-sell. If you&rsquo;re a fit, we&rsquo;ll invite you to the next step.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3 text-white/60 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#14E0BF] shrink-0" />
                  Reply within 48 hours
                </div>
                <div className="flex items-center gap-3 text-white/60 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#14E0BF] shrink-0" />
                  No upfront commitment
                </div>
                <div className="flex items-center gap-3 text-white/60 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#14E0BF] shrink-0" />
                  Real human reviews every application
                </div>
              </div>

              <div className="mt-10 pt-10 border-t border-white/10">
                <p className="text-white/45 text-sm mb-4">
                  Or skip the form and book a 15-minute intro call.
                </p>
                <button
                  type="button"
                  onClick={() => toast.info("A calendar invite would open here")}
                  className="btn-pill inline-flex items-center gap-2 border border-white/20 hover:border-[#14E0BF] hover:text-[#14E0BF] text-white font-display text-[12px] uppercase tracking-wider px-5 py-3 rounded-full"
                >
                  <Calendar className="w-4 h-4" />
                  Book a 15-min Call
                </button>
              </div>
            </div>
          </div>

          {/* Right */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="bg-[#14141B] rounded-[28px] p-10 lg:p-14 text-center animate-scale-in border border-[#14E0BF]/30 teal-glow-strong">
                <div className="w-20 h-20 rounded-full bg-[#14E0BF] flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-[#07070A]" />
                </div>
                <h3 className="font-display text-white text-3xl uppercase mb-3">
                  Got it, {form.firstName}.
                </h3>
                <p className="text-white/70 max-w-[460px] mx-auto leading-relaxed">
                  Your application is with our partnerships team. Expect a reply within 48 hours – usually a lot sooner.
                </p>
                <button
                  onClick={() => {
                    setForm(initialState);
                    setSubmitted(false);
                  }}
                  className="btn-pill mt-8 inline-flex items-center gap-2 bg-[#14E0BF] text-[#07070A] font-display text-[13px] uppercase tracking-wider px-6 py-3.5 rounded-full"
                >
                  Submit another
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-[#14141B] border border-white/10 rounded-[28px] p-6 lg:p-10"
              >
                <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/8">
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/45">
                    Application Form
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#14E0BF]">
                    ≈ 60 seconds
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>First Name *</label>
                    <input
                      className={inputCls}
                      value={form.firstName}
                      onChange={(e) => update("firstName", e.target.value)}
                      placeholder="Forename"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Last Name *</label>
                    <input
                      className={inputCls}
                      value={form.lastName}
                      onChange={(e) => update("lastName", e.target.value)}
                      placeholder="Surname"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Business Name</label>
                    <input
                      className={inputCls}
                      value={form.businessName}
                      onChange={(e) => update("businessName", e.target.value)}
                      placeholder="Your takeaway / kitchen / restaurant name"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input
                      className={inputCls}
                      value={form.city}
                      onChange={(e) => update("city", e.target.value)}
                      placeholder="Manchester, Birmingham..."
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Postcode</label>
                    <input
                      className={inputCls}
                      value={form.postcode}
                      onChange={(e) => update("postcode", e.target.value)}
                      placeholder="M1 1AE"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Mobile *</label>
                    <input
                      className={inputCls}
                      value={form.mobile}
                      onChange={(e) => update("mobile", e.target.value)}
                      placeholder="07..."
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Where are you in your journey?</label>
                    <select
                      className={inputCls}
                      value={form.kitchenStatus}
                      onChange={(e) => update("kitchenStatus", e.target.value)}
                    >
                      <option value="">— Choose one —</option>
                      {KITCHEN_OPTIONS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>100% Halal kitchen?</label>
                    <div className="grid grid-cols-2 gap-3">
                      {["Yes", "No"].map((v) => (
                        <button
                          type="button"
                          key={v}
                          onClick={() => update("halal", v)}
                          className={`py-3.5 rounded-xl font-display text-sm uppercase tracking-wider border transition-colors ${
                            form.halal === v
                              ? "bg-[#14E0BF] border-[#14E0BF] text-[#07070A]"
                              : "bg-transparent border-white/15 text-white hover:border-[#14E0BF] hover:text-[#14E0BF]"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Alcohol-free premises?</label>
                    <div className="grid grid-cols-2 gap-3">
                      {["Yes", "No"].map((v) => (
                        <button
                          type="button"
                          key={v}
                          onClick={() => update("alcoholFree", v)}
                          className={`py-3.5 rounded-xl font-display text-sm uppercase tracking-wider border transition-colors ${
                            form.alcoholFree === v
                              ? "bg-[#14E0BF] border-[#14E0BF] text-[#07070A]"
                              : "bg-transparent border-white/15 text-white hover:border-[#14E0BF] hover:text-[#14E0BF]"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>How did you hear about us?</label>
                    <select
                      className={inputCls}
                      value={form.hearAbout}
                      onChange={(e) => update("hearAbout", e.target.value)}
                    >
                      <option value="">— Choose one —</option>
                      {HEAR_OPTIONS.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>
                      Anything we should know? (optional)
                    </label>
                    <textarea
                      rows={4}
                      className={inputCls}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Existing brands, sales numbers, ambitions..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-start gap-3 text-white/65 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1 w-5 h-5 rounded accent-[#14E0BF]"
                        checked={form.agree}
                        onChange={(e) => update("agree", e.target.checked)}
                      />
                      I agree to the kitchnworx privacy policy. We&rsquo;ll use this info only to evaluate your application.
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      className="btn-pill w-full inline-flex items-center justify-center gap-2 bg-[#14E0BF] hover:bg-[#0FB89C] text-[#07070A] font-display text-[14px] uppercase tracking-wider px-7 py-5 rounded-full"
                    >
                      Submit application
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JourneyForm;
